import { formatBedroomOptions } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { EnhancedImage } from "@/components/shared/EnhancedImage";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Bed, Bath, Square, Phone, Eye, IndianRupee, Rocket, Clock } from "lucide-react";
import { FavoriteButton } from "@/components/shared/FavoriteButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { InquiryForm } from "./InquiryForm";
import { PriceRequestForm } from "./PriceRequestForm";
import { AuthGatedDialog } from "@/components/shared/AuthGatedDialog";
import { Link } from "@/lib/router-compat";
import { formatPriceDisplay } from "@/utils/currency";
import { ListingTypeBadge, normalizeListingType } from "@/components/shared/ListingTypeBadge";
import { RentPriceSuffix } from "@/components/shared/RentPriceSuffix";
import defaultResidentialImage from "@/assets/default-residential-property.webp";
import defaultCommercialImage from "@/assets/default-commercial-property.webp";

interface Property {
  id: string;
  slug?: string;
  title: string;
  location?: string | null;
  locality?: string | null;
  city: string | null;
  price: number | null;
  price_min?: number | null;
  price_max?: number | null;
  property_type: string;
  area_sqft?: number | null;
  area_min?: number | null;
  area_max?: number | null;
  bedrooms?: number | null;
  bedrooms_options?: number[];
  bathrooms?: number | null;
  images?: string[] | null;
  description?: string | null;
  is_featured: boolean | null;
  price_on_request?: boolean;
  developer_name?: string | null;
  possession_stage?: string | null;
  listing_type?: string | null;
}

interface PropertyCardListProps {
  property: Property;
}

export const PropertyCardList = ({ property }: PropertyCardListProps) => {
  const getPropertyTypeLabel = (type: string) =>
    type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

  const getDefaultImage = (propertyType: string) => {
    const isCommercial = ['commercial', 'office', 'retail', 'warehouse', 'industrial'].some(t =>
      propertyType.toLowerCase().includes(t)
    );
    return isCommercial ? defaultCommercialImage : defaultResidentialImage;
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="relative w-full sm:w-56 md:w-64 h-48 sm:h-auto shrink-0 bg-muted">
          <EnhancedImage
            src={property.images?.[0] || getDefaultImage(property.property_type)}
            alt={property.title}
            className="w-full h-full object-cover"
            enableEnhancement={!!(property.images && property.images.length > 0)}
            optimizedWidth={480}
            sizes="(max-width: 640px) 100vw, 300px"
          />
          <div className="absolute top-2 left-2 flex flex-wrap gap-1.5 pr-10">
            <ListingTypeBadge listingType={property.listing_type} />
            {property.is_featured && <Badge className="bg-primary">Featured</Badge>}
            {property.possession_stage === 'new-launch' && (
              <Badge className="bg-emerald-600 text-white">
                <Rocket className="h-3 w-3 mr-1" />New Launch
              </Badge>
            )}
            {property.possession_stage === 'under-construction' && (
              <Badge className="bg-amber-500 text-white">
                <Clock className="h-3 w-3 mr-1" />Under Construction
              </Badge>
            )}
          </div>
          <div className="absolute top-2 right-2">
            <FavoriteButton propertyId={property.id} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-lg truncate">
                <Link to={`/property/${property.slug ?? property.id}`} className="hover:underline">
                  {property.title}
                </Link>
              </h3>
              <Badge variant="secondary" className="shrink-0">{getPropertyTypeLabel(property.property_type)}</Badge>
            </div>

            <div className="flex items-center text-muted-foreground text-sm mb-2">
              <MapPin className="h-3 w-3 mr-1 shrink-0" />
              <span className="truncate">
                {property.location || property.locality || ''}{(property.location || property.locality) && property.city ? ', ' : ''}{property.city || ''}
              </span>
            </div>

            <div className="text-2xl font-bold text-primary mb-2">
              {formatPriceDisplay({
                price_on_request: property.price_on_request,
                price: property.price,
                price_min: property.price_min ?? null,
                price_max: property.price_max ?? null,
              })}
              <RentPriceSuffix listingType={property.listing_type} priceOnRequest={property.price_on_request} />
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-foreground mb-2">
              {(property.area_min != null && property.area_max != null) ? (
                <div className="flex items-center"><Square className="h-3 w-3 mr-1" />{property.area_min} - {property.area_max} sq ft</div>
              ) : property.area_sqft ? (
                <div className="flex items-center"><Square className="h-3 w-3 mr-1" />{property.area_sqft} sq ft</div>
              ) : null}
              {(property as any).bedrooms_options?.length > 0 ? (
                <div className="flex items-center"><Bed className="h-3 w-3 mr-1" />{formatBedroomOptions((property as any).bedrooms_options as number[])}</div>
              ) : property.bedrooms ? (
                <div className="flex items-center"><Bed className="h-3 w-3 mr-1" />{property.bedrooms} bed</div>
              ) : null}
              {property.bathrooms && (
                <div className="flex items-center"><Bath className="h-3 w-3 mr-1" />{property.bathrooms} bath</div>
              )}
            </div>

            {property.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">{property.description}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
            <Link to={`/property/${property.slug ?? property.id}`}>
              <Button variant="outline" size="sm"><Eye className="h-3 w-3 mr-1" />View</Button>
            </Link>
            {property.price_on_request ? (
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm"><IndianRupee className="h-3 w-3 mr-1" />Request Price</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Request Price Information</DialogTitle></DialogHeader>
                  <PriceRequestForm propertyId={property.id} propertyTitle={property.title} />
                </DialogContent>
              </Dialog>
            ) : (
              <AuthGatedDialog
                trigger={<Button size="sm"><Phone className="h-3 w-3 mr-1" />Inquire</Button>}
                title="Property Inquiry"
              >
                <InquiryForm propertyId={property.id} propertyTitle={property.title} />
              </AuthGatedDialog>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
